import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Send, RefreshCcw, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { uploadOne } from "@/lib/uploads";

/**
 * Weekly Project Status Report editor.
 *
 * `project_id` is required — the modal will call
 * `POST /api/reports/weekly/generate/{project_id}` on open to auto-populate
 * every field from live data. CRM Executive edits, then Sends.
 */
export default function WeeklyReportModal({ projectId, onClose, onSent }) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reports/weekly/generate/${projectId}`);
      setRep(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { generate(); }, [generate]);

  const patchField = (field, value) => setRep((r) => ({ ...r, [field]: value }));
  const patchStage = (idx, field, value) => setRep((r) => ({
    ...r,
    stages: r.stages.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
  }));

  const save = async () => {
    if (!rep) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/reports/weekly/${rep.id}`, {
        client_name: rep.client_name, project_name: rep.project_name,
        current_phase: rep.current_phase, report_date: rep.report_date,
        city: rep.city, handover_date: rep.handover_date,
        carcass_delivery_date: rep.carcass_delivery_date,
        stages: rep.stages,
        material_selection_pending: rep.material_selection_pending,
        delay_log: rep.delay_log, looking_ahead: rep.looking_ahead,
        footer_left: rep.footer_left, footer_right: rep.footer_right,
      });
      setRep(data);
      toast.success("Draft saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const buildPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.text("Weekly Project Status", 40, 50);
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Client: ${rep.client_name}    Project: ${rep.project_name}`, 40, 72);
    doc.text(`Current Phase: ${rep.current_phase || "—"}    Date: ${rep.report_date || ""}`, 40, 86);
    doc.text(`City: ${rep.city || "—"}    Handover: ${rep.handover_date || "TBD"}    Carcass Delivery: ${rep.carcass_delivery_date || "TBD"}`, 40, 100);

    autoTable(doc, {
      startY: 120,
      head: [["Stage", "Status", "Completion %", "Notes"]],
      body: rep.stages.map((s) => [s.label, s.status || "—", `${s.completion_pct ?? 0}%`, s.notes || ""]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
    });

    let y = doc.lastAutoTable.finalY + 20;
    const section = (title, text) => {
      doc.setFont("helvetica", "bold"); doc.text(title, 40, y); y += 14;
      doc.setFont("helvetica", "normal");
      const wrapped = doc.splitTextToSize(text || "—", 515);
      doc.text(wrapped, 40, y); y += wrapped.length * 12 + 8;
    };
    section("Material Selection Pending", rep.material_selection_pending);
    section("Delay Log & Root Cause Analysis", rep.delay_log);
    section("Looking Ahead", rep.looking_ahead);

    // Footer
    y = Math.max(y + 10, 680);
    doc.setDrawColor(200); doc.line(40, y, 555, y); y += 14;
    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("Site Installation Progress Sequence", 40, y);
    doc.text("Production / Manufacturing Progress Sequence", 300, y);
    doc.setFont("helvetica", "normal"); y += 12;
    const leftLines = doc.splitTextToSize(rep.footer_left || "", 250);
    const rightLines = doc.splitTextToSize(rep.footer_right || "", 250);
    doc.text(leftLines, 40, y);
    doc.text(rightLines, 300, y);

    return doc.output("blob");
  };

  const send = async () => {
    if (!rep) return;
    setSending(true);
    try {
      await save();
      const blob = buildPDF();
      const file = new File([blob], `weekly-report-${rep.report_date}.pdf`, { type: "application/pdf" });
      const uploaded = await uploadOne(file);
      const { data } = await api.post(`/reports/weekly/${rep.id}/send`, {
        pdf_file_id: uploaded.id,
      });
      toast.success(data.email_delivered ? "Report sent + emailed" : "Report saved and marked sent");
      onSent?.();
      onClose?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="weekly-report-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · Weekly Report</p>
            <h3 className="font-display text-xl text-white mt-0.5">Weekly Project Status</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="weekly-report-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {loading || !rep ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Building draft…
          </div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-5">
            {/* Header */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Client Name" value={rep.client_name} onChange={(v) => patchField("client_name", v)} tid="wr-client"/>
              <Field label="Project Name" value={rep.project_name} onChange={(v) => patchField("project_name", v)} tid="wr-project"/>
              <Field label="Current Phase" value={rep.current_phase} onChange={(v) => patchField("current_phase", v)} tid="wr-phase"/>
              <Field label="Date" value={rep.report_date} onChange={(v) => patchField("report_date", v)} type="date" tid="wr-date"/>
              <Field label="City" value={rep.city} onChange={(v) => patchField("city", v)} tid="wr-city"/>
              <Field label="Handover Date" value={rep.handover_date} onChange={(v) => patchField("handover_date", v)} type="date" tid="wr-handover"/>
              <Field label="Carcass Delivery Date" value={rep.carcass_delivery_date} onChange={(v) => patchField("carcass_delivery_date", v)} type="date" tid="wr-carcass"/>
              <Field label="Client Email" value={rep.recipient_email || ""} onChange={(v) => patchField("recipient_email", v)} tid="wr-email"/>
            </div>

            {/* Stage table */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Stage Table</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[720px]">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                    <tr><th className="text-left py-2 px-2">Stage</th><th className="text-left py-2 px-2">Status</th><th className="text-left py-2 px-2 w-24">%</th><th className="text-left py-2 px-2">Notes</th></tr>
                  </thead>
                  <tbody>
                    {rep.stages.map((s, i) => (
                      <tr key={s.key} className="border-b border-[#161616]">
                        <td className="py-2 px-2 text-white">{s.label}</td>
                        <td className="py-2 px-2">
                          <input value={s.status || ""} onChange={(e) => patchStage(i, "status", e.target.value)}
                                 className="iz-input w-full !py-1 text-xs" data-testid={`wr-stage-status-${i}`}/>
                        </td>
                        <td className="py-2 px-2">
                          <input type="number" min="0" max="100" value={s.completion_pct ?? 0}
                                 onChange={(e) => patchStage(i, "completion_pct", parseInt(e.target.value) || 0)}
                                 className="iz-input w-full !py-1 text-xs" data-testid={`wr-stage-pct-${i}`}/>
                        </td>
                        <td className="py-2 px-2">
                          <input value={s.notes || ""} onChange={(e) => patchStage(i, "notes", e.target.value)}
                                 className="iz-input w-full !py-1 text-xs" data-testid={`wr-stage-notes-${i}`}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Manual sections */}
            <TextArea label="Material Selection Pending"
                      value={rep.material_selection_pending}
                      onChange={(v) => patchField("material_selection_pending", v)}
                      tid="wr-materials"/>
            <TextArea label="Delay Log & Root Cause Analysis"
                      value={rep.delay_log}
                      onChange={(v) => patchField("delay_log", v)}
                      tid="wr-delay"/>
            <TextArea label="Looking Ahead"
                      value={rep.looking_ahead}
                      onChange={(v) => patchField("looking_ahead", v)}
                      tid="wr-ahead"/>

            {/* Footer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-[#1a1a1a] pt-4">
              <TextArea label="Site Installation Progress Sequence"
                        value={rep.footer_left}
                        onChange={(v) => patchField("footer_left", v)}
                        rows={7} tid="wr-footer-left"/>
              <TextArea label="Production / Manufacturing Progress Sequence"
                        value={rep.footer_right}
                        onChange={(v) => patchField("footer_right", v)}
                        rows={7} tid="wr-footer-right"/>
            </div>
          </div>
        )}

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="iz-btn-secondary text-xs" data-testid="weekly-report-cancel">Cancel</button>
          <button onClick={generate} disabled={loading} className="iz-btn-secondary text-xs flex items-center gap-1"
                  data-testid="weekly-report-regenerate">
            <RefreshCcw className="w-3.5 h-3.5"/> Refresh from live data
          </button>
          <button onClick={save} disabled={saving || !rep} className="iz-btn-secondary text-xs" data-testid="weekly-report-save">
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button onClick={send} disabled={sending || !rep} className="iz-btn-primary text-xs flex items-center gap-1.5"
                  data-testid="weekly-report-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            Send Report
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", tid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="iz-input w-full text-xs" data-testid={tid}/>
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, tid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="iz-input w-full text-xs" data-testid={tid}/>
    </div>
  );
}

/** Pending Weekly Reports widget — for CRM Manager / Executive dashboards. */
export function WeeklyReportsPendingWidget({ onOpen }) {
  const [data, setData] = useState({ pending: [], total: 0, week_of: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/reports/weekly/pending");
        setData(data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (data.total === 0) return null;

  return (
    <div className="iz-card p-5 border-[#d4af37]/40" data-testid="weekly-reports-pending-widget">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#d4af37]"/>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Weekly Report Pending · {data.total}
        </p>
        <span className="ml-2 text-[10px] text-gray-500 italic">Week of {data.week_of}</span>
      </div>
      <div className="space-y-2">
        {data.pending.slice(0, 8).map((p) => (
          <button key={p.project_id} onClick={() => onOpen?.(p.project_id)}
                  className="w-full text-left border border-[#262626] hover:border-[#d4af37]/50 bg-[#0a0a0a] rounded-md p-3 flex items-center gap-3 transition-colors"
                  data-testid={`weekly-pending-${p.project_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{p.client_name}{p.city ? ` · ${p.city}` : ""}</p>
              <p className="text-[11px] text-gray-500 truncate">{p.project_name}</p>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] shrink-0">
              Send Report →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
